import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMonitorConfigStore } from './useMonitorConfigStore';
import { ScreenMap } from './ScreenMap';
import { RoleSelector } from './RoleSelector';
import { SyncOptionsPanel } from './SyncOptionsPanel';
import { LayoutPicker } from './LayoutPicker';
import type { MonitorManager } from './MonitorManager';
import { getViewer } from '../viewer/viewerRegistry';
import { mapScreens } from './screenUtils';

interface MonitorConfigDialogProps {
  open: boolean;
  onClose: () => void;
  manager: MonitorManager;
  /** Active viewer ID — used to show which sync features are available. */
  activeViewerId?: string;
}

export function MonitorConfigDialog({ open, onClose, manager, activeViewerId }: MonitorConfigDialogProps) {
  const {
    screens,
    configs,
    syncPreferences,
    permissionState,
    loading,
    setScreenRole,
    setScreenLayout,
    setSyncPreference,
    setPermissionState,
    setScreens,
    save,
    load,
    reset,
  } = useMonitorConfigStore();

  const [selectedScreen, setSelectedScreen] = useState<string | null>(null);
  const [testingLayout, setTestingLayout] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const handleRequestPermission = useCallback(async () => {
    setRequesting(true);
    try {
      const granted = await manager.requestPermission();
      if (granted) {
        setPermissionState('granted');
        const detected = manager.getScreens();
        setScreens(mapScreens(detected));
        load();
      }
    } finally {
      setRequesting(false);
    }
  }, [manager, load, setPermissionState, setScreens]);

  // Load saved config and detect screens when dialog opens
  useEffect(() => {
    if (open) {
      // Load saved config first, then detect screens so setScreens
      // can merge with (not be overwritten by) persisted configs.
      load().then(() => {
        manager.checkCapability().then((cap) => {
          setPermissionState(cap.permissionGranted ? 'granted' : 'prompt');
          if (cap.permissionGranted) {
            const detected = manager.getScreens();
            if (detected.length > 0) {
              setScreens(mapScreens(detected));
            }
          }
        });
      });
    }
  }, [open, load, manager, setPermissionState, setScreens]);

  // Auto-select first screen
  useEffect(() => {
    if (screens.length > 0 && !selectedScreen) {
      setSelectedScreen(screens[0].label);
    }
  }, [screens, selectedScreen]);

  const selectedConfig = configs.find((c) => c.screenLabel === selectedScreen);

  // Build role map for ScreenMap display
  const roleMap: Record<string, string> = {};
  for (const c of configs) {
    roleMap[c.screenLabel] = c.role;
  }

  const handleSave = async () => {
    await save();
    onClose();
  };

  const handleTestLayout = async () => {
    setTestingLayout(true);
    try {
      const detectedScreens = manager.getScreens();
      for (const config of configs) {
        if (config.role === 'worklist' || config.role === 'unassigned') continue;
        const screen = detectedScreens.find((s) => s.label === config.screenLabel);
        if (!screen) continue;
        // Open a test window on each assigned screen
        manager.openViewerOnScreen(screen, '', '', config.role);
      }
    } finally {
      // Auto-close test windows after 5 seconds
      setTimeout(() => {
        manager.closeAll();
        setTestingLayout(false);
      }, 5000);
    }
  };

  const handleReset = () => {
    reset();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Monitor Configuration</DialogTitle>
      <DialogContent dividers>
        {permissionState !== 'granted' && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={requesting}
                onClick={handleRequestPermission}
              >
                {requesting ? 'Requesting…' : 'Enable'}
              </Button>
            }
          >
            Window management permission not granted. Click "Enable" to allow
            multi-monitor support.
          </Alert>
        )}

        {screens.length === 0 && permissionState === 'granted' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No additional screens detected. Connect a second monitor and
            it will appear here.
          </Alert>
        )}

        {/* Screen Map */}
        {screens.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Your Monitors
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Click a monitor to assign its role.
            </Typography>
            <ScreenMap
              screens={screens}
              selectedLabel={selectedScreen}
              roles={roleMap}
              onSelect={setSelectedScreen}
            />
          </>
        )}

        {/* Selected screen config */}
        {selectedScreen && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {selectedScreen}
            </Typography>
            <RoleSelector
              screenLabel={selectedScreen}
              currentRole={selectedConfig?.role ?? 'unassigned'}
              onChange={setScreenRole}
            />
            <LayoutPicker
              screenLabel={selectedScreen}
              rows={selectedConfig?.defaultLayout.rows ?? 1}
              cols={selectedConfig?.defaultLayout.cols ?? 1}
              onChange={setScreenLayout}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Sync Preferences */}
        <SyncOptionsPanel
          preferences={syncPreferences}
          onChange={setSyncPreference}
          capabilities={activeViewerId ? getViewer(activeViewerId)?.syncCapabilities : undefined}
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleTestLayout}
          disabled={testingLayout || screens.length < 2}
          startIcon={testingLayout ? <CircularProgress size={16} /> : undefined}
        >
          {testingLayout ? 'Testing…' : 'Test Layout'}
        </Button>
        <Button onClick={handleReset} color="warning">
          Reset to Default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
